/**
 *
 * Input
 *
 */
import React, { memo, ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import { validateEmail } from 'core/utils/constants/regex';
import moment from 'moment';


const MAX_LENGTH = 50;

const STRING_NUMERIC_FIELD_SUFFIXES = ['PINCODE', 'MOBILE_NUMBER'];

const shouldKeepAsString = (name?: string) =>
  !!name &&
  STRING_NUMERIC_FIELD_SUFFIXES.some(fieldName =>
    name.split('.').pop() === fieldName,
  );

const normalizeValue = (value: any, name?: string) => {
  if (value === null || value === undefined) return '';
  return shouldKeepAsString(name) ? String(value) : value;
};


// Function to restrict input to numeric characters only
const numOnly = event => {
  const key = event.keyCode || event.which;
  if (
    (key >= 48 && key <= 57) || // Numeric keys (0-9)
    (key >= 96 && key <= 105) || // Numeric keypad keys (0-9)
    [8, 9, 13, 35, 36, 37, 39].includes(key) || // Control keys
    (event.ctrlKey === true && [65, 67, 86, 88, 90].includes(key)) // Ctrl+ shortcuts
  ) {
    return;
  } else {
    event.preventDefault();
  }
};

// Function to clean input value to allow only numeric characters
const cleanNumericValue = (event) => {
  const input = event.target;
  input.value = input.value.replace(/[^0-9]/g, '');
};

interface Props extends StyleComponentProps {
  placeholder?: string;
  label?: string | any;
  type?: string;
  required?: boolean;
  value: string | number | undefined | any;
  message?: string;
  error?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement> | undefined;
  onFocus?: any;
  className?: string;
  rightIcon?: ReactNode;
  name?: string;
  disabled?: boolean;
  defaultValue?: string | number | undefined;
  labelClassName?: string;
  pattern?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  min?: string;
  max?: string;
  onKeyDown?: any;
  onKeyPress?: any;
  readonly?: any;
  requiredlabelClassName?: string;
  allowOnlyNumbers?: boolean;
  maxLength?: number;
  restrictCopyPaste?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

export const Input = memo((props: Props) => {
  const [defaultError, setdefaultError] = React.useState<string | undefined>(
    undefined,
  );
  const [lengthError, setLengthError] = React.useState<string | undefined>(
    undefined,
  );

  const errorTimeoutRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedValueRef = useRef<string>();
  const inputName = props.name;
  const inputValue = props.value;
  const onInputChange = props.onChange;

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      shouldKeepAsString(inputName) &&
      typeof inputValue === 'number' &&
      onInputChange &&
      inputRef.current &&
      normalizedValueRef.current !== `${inputName}:${inputValue}`
    ) {
      normalizedValueRef.current = `${inputName}:${inputValue}`;
      onInputChange({
        target: inputRef.current,
        currentTarget: inputRef.current,
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [inputName, inputValue, onInputChange]);

  // Add effect to handle wheel events on number inputs
  useEffect(() => {
    const inputElement = inputRef.current;

    if (inputElement && props.type === 'number') {
      const handleWheel = (e: WheelEvent) => {
        // Prevent scroll only when the input is focused
        if (document.activeElement === inputElement) {
          e.preventDefault();
        }
      };

      inputElement.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        inputElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [props.type]);

  useEffect(() => {
    if (props.type === 'email') {
      const x = validateEmail(props.value as string);

      if (!x) setdefaultError('Please input a valid email address.');
      else setdefaultError(undefined);

      if (props.value && !(props.value as string).length) {
        setdefaultError(undefined);
      }
      if (!props.value) {
        setdefaultError(undefined);
      }
    }
  }, [props.type, props.value]);

  function formatDate(value) {
    if (shouldKeepAsString(props.name)) {
      return normalizeValue(value, props.name);
    }

    if (["datetime-local"]?.includes(props?.type as any)) {
      const now = moment(value);
      return now.format('YYYY-MM-DDTHH:mm');
    } else if (["date"]?.includes(props?.type as any)) {
      const now = moment(value);
      return now.format('YYYY-MM-DD');
    }
    return value;
  }

  const clearErrorWithTimeout = () => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = setTimeout(() => {
      setLengthError(undefined);
    }, 4000);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent arrow keys from changing number values and general arrow navigation
    if (props.type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }

    if (props.allowOnlyNumbers) {
      numOnly(e);
    }

    const maxLength = props.maxLength || MAX_LENGTH;  // Use default MAX_LENGTH if not provided

    if (maxLength &&
      props.value?.toString().length >= maxLength &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      if (e.key.length === 1) { // Only for character keys, not control keys
        setLengthError(`Maximum ${maxLength} characters allowed`);
        clearErrorWithTimeout()
        e.preventDefault();
      }
    } else {
      setLengthError(undefined);
    }

    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const handleInput = props.allowOnlyNumbers ? cleanNumericValue : props.onChange;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.value;
    const maxLength = props.maxLength || MAX_LENGTH;  // Use default MAX_LENGTH if not provided

    if (maxLength && newValue.length > maxLength) {
      e.target.value = newValue.slice(0, maxLength);
      setLengthError(`Maximum ${maxLength} characters allowed`);
      clearErrorWithTimeout()
    } else {
      setLengthError(undefined);
    }

    if (props.onChange) {
      if (shouldKeepAsString(props.name)) {
        e.target.value = String(e.target.value);
      }
      props.onChange(e);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const maxLength = props.maxLength || MAX_LENGTH;
    const pastedText = e.clipboardData.getData('text');

    if (props.allowOnlyNumbers && /[^0-9]/.test(pastedText)) {
      e.preventDefault();
      const input = e.currentTarget;
      const cleanedText = pastedText.replace(/[^0-9]/g, '');
      const nextValue =
        input.value.slice(0, input.selectionStart || 0) +
        cleanedText +
        input.value.slice(input.selectionEnd || input.value.length);

      const inputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;
      inputValueSetter?.call(input, maxLength ? nextValue.slice(0, maxLength) : nextValue);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    if (maxLength) {
      const currentLength = props.value?.toString().length || 0;

      if (currentLength + pastedText.length > maxLength) {
        e.preventDefault();
        setLengthError(`Maximum ${maxLength} characters allowed`);
        clearErrorWithTimeout()
      } else {
        setLengthError(undefined);
      }
    }

    if (props.onPaste) {
      props.onPaste(e);
    }
  };

  return (
    <div
      onClick={props.onClick}
      className={`relative inline-block
      ${props.width ? props.width : 'w-full'}  my-2
      ${props.margin ? props.margin : 'm-auto'}  ${props.className}`}
    >
      {props.label !== undefined && (
        <label
          className={`inline-block flex ${props.labelClassName} mb-1 text-sm font-[500]`}
        >
          {props.label}
          {props.required && (
            <span className={`text-secondary-red ${props.requiredlabelClassName}`}>
              *
            </span>
          )}
        </label>
      )}
      <input
        ref={inputRef}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        {...props?.onKeyPress}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        pattern={props.pattern}
        name={props.name}
        readOnly={props.readonly}
        value={formatDate(props.value)}
        onChange={handleChange}
        disabled={props.disabled}
        placeholder={props.placeholder || ""}
        min={props.min}
        max={props.max}
        inputMode={props.inputMode || (props.allowOnlyNumbers ? 'numeric' : undefined)}
        maxLength={props.maxLength || MAX_LENGTH}
        defaultValue={props.defaultValue}
        type={props.type ? props.type : 'text'}
        className={classNames(
          `${props.disabled ? "bg-[#F2F2F2]" : "bg-neutral-light"} w-full
       border-[1px] border-neutral-40 border-solid ${(props.error || lengthError) && 'border-secondary-red'
          } ${props.height ? props.height : 'h-[44px]'}  rounded-[6px] ${props.padding || 'p-[12px]'
          } focus:outline-4 focus:outline-primary-40 focus:border-[1px] focus:border-primary-main ${props.className
          }`,
          {},
        )}

        onCopy={props.restrictCopyPaste ? (e) => e.preventDefault() : undefined}
        onPaste={props.restrictCopyPaste ? (e) => e.preventDefault() : (e) => handlePaste(e)}
        onCut={props.restrictCopyPaste ? (e) => e.preventDefault() : undefined}
      />
      {props?.rightIcon}
      {defaultError && (
        <span
          className={classNames(
            `text-secondary-red mt-[4px] text-xs`
          )}
        >
          {defaultError}
        </span>
      )}
      {lengthError && (
        <span
          className={classNames(
            `text-secondary-red mt-[4px] text-xs`
          )}
        >
          {lengthError}
        </span>
      )}
      {props.message && !lengthError && (
        <span
          className={classNames(
            `${props.error ? 'text-secondary-red' : 'text-neutral-60'}
            mt-[4px] text-xs
            `,
          )}
        >
          {props.message}
        </span>
      )}
    </div>
  );
});
